import * as React from "react";
import { Link } from "react-router-dom";
import { FieldNav } from "../components/FieldNav.js";
import { FieldFooter } from "../components/FieldFooter.js";
import { useDocumentTitle } from "../hooks/useDocumentTitle.js";
import { SEO_TITLES } from "../lib/seo-titles.js";
import "../styles/landing-field.css";
import "../styles/content-pages-field.css";

/**
 * /compare — an honest, category-level comparison of the common ways kids
 * practice math (worksheets, flashcard apps, 1:1 tutoring, game-based practice).
 * Deliberately does NOT name or make claims about specific competitors — those
 * require sourced, current, legally-vetted facts we don't have in-repo — so it
 * compares approaches, not brands. Captures "best way to practice math" /
 * "math games vs worksheets" intent truthfully. Same content-page shell, no
 * bespoke CSS.
 */
export function CompareRoute(): React.ReactElement {
  useDocumentTitle(SEO_TITLES["/compare"]);
  return (
    <div className="v-field">
      <FieldNav />
      <main id="main" className="v-field-c__page">
        <div className="container">
          <span className="v-field-c__eyebrow">Compare</span>
          <h1 className="v-field-c__title">
            Math games vs. worksheets, flashcards &amp; <span className="stripe">tutoring.</span>
          </h1>
          <p className="v-field-c__lede">
            There are four common ways kids practice math outside class. Here is an honest look at
            what each is good at — and where game-based practice fits.
          </p>
          <div className="v-field-c__prose">
            <h2>Worksheets &amp; printables</h2>
            <p>
              Cheap and standards-aligned, and fine for a specific skill on a specific day. The
              trade-off is engagement and feedback: a worksheet can&#39;t adapt to what a student
              already knows, and the answers arrive later (if at all), so mistakes harden before
              anyone catches them.
            </p>
            <h2>Flashcard &amp; drill apps</h2>
            <p>
              Great for speed on math facts — addition, multiplication tables, quick recall. They
              tend to stop at facts, though: multi-step reasoning, word problems, and grade-level
              standards usually sit outside what a flashcard loop can teach.
            </p>
            <h2>1:1 tutoring</h2>
            <p>
              The most personalized option, and the most effective when a student needs a human to
              unstick them — but also by far the most expensive, the hardest to schedule, and
              limited to the hour it runs.
            </p>
            <h2>Game-based practice</h2>
            <p>
              This is our approach. Every level is a real curriculum problem inside a fast,
              head-to-head game, so kids choose to keep practicing. A free five-minute placement
              test tailors the difficulty, the questions cover full grade-level standards (not just
              facts), and it costs a fraction of tutoring. The trade-off is honest too: it is
              practice and reinforcement, not a replacement for a teacher.
            </p>
            <h2>Where Sports Academy Games fits</h2>
            <p>
              Use it as the daily-practice layer between class and test day: it keeps kids drilling
              the right skills without a fight, adapts as they improve, and doubles as{" "}
              <Link to="/test-prep">test prep</Link>. Start with{" "}
              <Link to="/practice">free practice by grade</Link>, browse the{" "}
              <Link to="/games">math games</Link>, or see{" "}
              <Link to="/plans">plans &amp; pricing</Link>.
            </p>
          </div>
          <div className="v-field-c__actions">
            <Link className="btn-field-primary" to="/practice">
              Try free practice
            </Link>
            <Link className="btn-field-secondary" to="/games">
              Browse the games
            </Link>
          </div>
        </div>
      </main>
      <FieldFooter />
    </div>
  );
}
